import React, { useCallback, useEffect, useLayoutEffect } from 'react';

import {
  endActivatingContent,
  openRequest,
  releaseForPageUnmount,
  setActivatingPhase,
  type ActivationPhase,
  type Outcome,
  type SummaryOptions,
} from './onboardingModalStore';
import { preloadOnboardingModal } from './OnboardingModalLayer';

export type {
  ActivationPhase,
  ComparisonView,
  OnboardingModalState,
  SummaryOptions,
} from './onboardingModalStore';

/**
 * The onboarding page's one design-system `Modal` (`onboarding-persistent-scene-launch-modals`,
 * R1): Launch's changes check, its summary and comparison, and the leave prompt, asked for with the
 * same promise shape as `useConfirm`'s `confirm` / `alert`, so the code awaiting them is unchanged.
 *
 * One Modal rather than `TLDialog` because the wash stands down only for the design-system Modal
 * (`body:has(.tl-modal-root) .wash`), so a `TLDialog` over a washed step showed two frosted layers.
 *
 * Page-local on purpose, not a general `useConfirm` on `Modal`: that is the design-system epic's
 * `TLDialog` retirement, and building it here would be a second copy the moment that batch lands.
 *
 * **The Modal outlives the page** (`onboarding-persistent-scene-launch-into-live`, R2). The state
 * lives in `onboardingModalStore`, and `OnboardingModalLayer` in the protected layout renders it, so
 * the page renders nothing for it. A summary opened with `carry: true` (the landing's) stays open
 * across the route change into the live viewer until it is dismissed; every other request still
 * settles as cancel when the page unmounts.
 *
 * **Launch's activating content** (`onboarding-persistent-scene-spinner`, R3) is not a request: the
 * page passes the run's phase as `activating`, and the Modal shows it whenever no request is
 * pending. A pending question or summary always wins (R3.1), so the changes check, the activating
 * content and the summary are one open Modal changing content.
 */
export function useOnboardingModal({
  activating = null,
}: {
  /** The Go Live run's phase while the wizard is locked for it; null when no run is activating. */
  activating?: ActivationPhase | null;
} = {}) {
  // A layout effect, so the activating content and a summary requested in the same batch reach the
  // layer in the same commit (spinner R3.3), never a frame apart.
  useLayoutEffect(() => {
    setActivatingPhase(activating);
  }, [activating]);

  /**
   * The run is over (release b). Called by the page in Go Live's `finally`: a run superseded
   * between the accept and the re-lock never re-locks, and would otherwise strand a Modal nothing
   * can dismiss.
   */
  const endActivating = useCallback(() => endActivatingContent(), []);

  const confirmChanges = useCallback(
    (message: React.ReactNode) =>
      new Promise<boolean>(resolve => {
        openRequest({ kind: 'changes', message }, outcome => resolve(outcome === 'confirm'));
      }),
    [],
  );

  const showSummary = useCallback(
    ({ carry = false, ...options }: SummaryOptions & { carry?: boolean }) =>
      // Settles `'confirm'` when the person dismisses it (any of its buttons, Escape, the scrim),
      // `'cancel'` when it is released unseen (the page or the session went).
      new Promise<Outcome>(resolve => {
        openRequest({ kind: 'summary', ...options }, resolve, { carry });
      }),
    [],
  );

  const confirmLeave = useCallback(
    () =>
      new Promise<boolean>(resolve => {
        openRequest({ kind: 'leave' }, outcome => resolve(outcome === 'confirm'));
      }),
    [],
  );

  // A window pending when the page unmounts (browser back, a session-expiry redirect) would leave
  // its promise unsettled for ever, so the awaiting function's `finally` never runs (R7). A carried
  // summary is the exception: it stays until dismissed (launch-into-live R2.2).
  useEffect(() => () => releaseForPageUnmount(), []);

  // The Modal's content is a lazy chunk (launch-into-live R2.4). Fetch it while the wizard is idle,
  // so the first Modal of the session (often Go Live's) never waits on the network or meets a
  // chunk a deploy has since replaced.
  useEffect(() => {
    void preloadOnboardingModal();
  }, []);

  return { confirmChanges, showSummary, confirmLeave, endActivating };
}
